import { useCubeQuery } from '@cubejs-client/react';
import { FileCopyRounded } from '@mui/icons-material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { ascend, isNotEmpty, isNotNil, map, pipe, prop, sort } from 'ramda';
import { useTranslation } from 'react-i18next';

import { ContractDetailsData, ContractVariant, CubeQueryData, I18nNamespace } from '~/common/types';
import { DimContractField } from '~/common/types/cubes/DimContract';
import { DimContractItemField } from '~/common/types/cubes/DimContractItem';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';

import { ContractDetails } from './components/ContractDetails/ContractDetails';

export const ContractDetailsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDetailsWidget' });

  const contractDetailsQuery = useCubeQuery<CubeQueryData>({
    filters: [
      {
        member: DimContractField.ID,
        operator: 'equals',
        values: [entityId],
      },
    ],
    dimensions: [
      DimContractField.VARIANT,
      DimContractField.PLANNED_VALUE,
      DimContractItemField.CURRENCY,
      DimContractItemField.LINE_ITEM_NUMBER,
      DimContractItemField.ID,
      DimContractItemField.UNIT_ID,
      DimContractItemField.TITLE,
      DimContractItemField.DESCRIPTION,
      DimContractItemField.PLANNED_QUANTITY,
      DimContractItemField.PLANNED_VALUE,
      DimContractItemField.PRICE_PER_UNIT,
    ],
  });

  const contractDetailsItems: CubeQueryData[] | undefined = contractDetailsQuery.resultSet?.rawData();

  const hasData = isNotNil(contractDetailsItems) && isNotEmpty(contractDetailsItems);

  const segmentContractValue = contractDetailsItems?.[0]?.[DimContractField.PLANNED_VALUE];
  const contractDetails: ContractDetailsData | undefined = hasData
    ? {
        plannedContractValue: segmentContractValue ? parseFloat(segmentContractValue) : undefined,
        contractVariant: contractDetailsItems[0][DimContractField.VARIANT] as ContractVariant,
        items: pipe(
          map((item: CubeQueryData) => ({
            id: item[DimContractItemField.ID],
            position: parseInt(item[DimContractItemField.LINE_ITEM_NUMBER]),
            title: item[DimContractItemField.TITLE],
            description: item[DimContractItemField.DESCRIPTION],
            unit: item[DimContractItemField.UNIT_ID],
            currency: item[DimContractItemField.CURRENCY],
            pricePerUnit: Number(item[DimContractItemField.PRICE_PER_UNIT]),
            plannedQuantity: parseInt(item[DimContractItemField.PLANNED_QUANTITY]),
            plannedVolume: item[DimContractItemField.PLANNED_VALUE],
          })),
          sort(ascend(prop('position'))),
        )(contractDetailsItems),
      }
    : undefined;

  const isLoading = contractDetailsQuery.isLoading;

  let content = <ContractDetails loading={isLoading} data={contractDetails} />;

  if (!hasData && !isLoading) {
    content = <PlaceholderWidget withImage />;
  }

  return (
    <Widget hover={false} data-intercom-target="ContractDetailsWidget">
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FileCopyRounded fontSize="small" />} />} />
      <WidgetContent>
        <div>{content}</div>
      </WidgetContent>
    </Widget>
  );
};
